// pages/payroll/EmployeeLoanReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = "http://nissi-india.com"; // change if needed

const n = (v) => (v === null || v === undefined ? "" : String(v));

const money = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return n(v);
  return x.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EmployeeLoanReportPrint = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  // ✅ keep filters in URL (optional)
  const finYearId = params.get("finYearId") || localStorage.getItem("finYearId") || "";
  const branchId = params.get("branchId") || localStorage.getItem("branchId") || "";
  const branchType = params.get("branchType") || localStorage.getItem("branchType") || "HO";

  const site = params.get("site") || "Select";
  const empcode = params.get("empcode") || "";
  const empname = params.get("empname") || "";
  const fromdate = params.get("fromdate") || ""; // YYYY-MM-DD ok (node converts)
  const todate = params.get("todate") || ""; // YYYY-MM-DD ok

  // ✅ STATIC header (as you asked)
  const HEADER = useMemo(
    () => ({
      name: "Nissi Infotech",
      sub1: "Nissi",
      sub2: "CIVIL CONSTRUCTION",
      address: "3/42-12, Nagappa complex, kavundampalayam - 641030, Tamil Nadu.",
      phone: "9003744644",
      rightTop: "infraconsoft-implementation",
      // Logo: keep a simple placeholder. (If you have a real logo file, tell me path and I’ll plug it in.)
      logoText: "NISSI INFOTECH PVT LTD",
    }),
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const totalBalanceAmount = useMemo(() => {
    return rows.reduce((sum, r) => {
      const v = Number(r.fld_BalanceAmount);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [rows]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // ✅ ONLY data from server (header is static)
      const dataRes = await axios.get(`${BASE_URL}/api/employee-loan-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
          site,
          empcode,
          empname,
          fromdate,
          todate,
        },
      });

      if (dataRes?.data?.success) {
        setRows(dataRes.data.rows || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // optional: auto print after load
  useEffect(() => {
    if (!loading) {
      setTimeout(() => window.print(), 300);
    }
  }, [loading]);

  return (
    <div className="print-root">
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
        .page { width: 100%; padding: 18px; }
        .outer { border: 2px solid #111; padding: 14px; }

        .companyBox {
          border: 2px solid #111;
          padding: 10px;
          display: grid;
          grid-template-columns: 140px 1fr;
          gap: 10px;
          align-items: center;
          margin-bottom: 14px;
          position: relative;
        }

        /* Logo placeholder like screenshot */
        .logoBlock {
          width: 130px;
          height: 85px;
          border: 2px solid #111;
          background: #f4f4f4;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 10px;
          font-weight: 800;
          padding: 6px;
          line-height: 1.1;
        }

        .rightTag {
          position: absolute;
          right: 10px;
          bottom: 8px;
          font-size: 11px;
          font-weight: 700;
        }

        .companyName {
          font-size: 22px;
          font-weight: 800;
          text-align: center;
          margin-bottom: 2px;
        }
        .companySub {
          text-align: center;
          font-size: 13px;
          line-height: 1.25;
        }
        .companySub strong { font-weight: 800; }

        .divider { border-top: 2px solid #111; margin: 10px 0; }

        .title {
          text-align: center;
          font-size: 18px;
          font-weight: 800;
          letter-spacing: 0.8px;
          margin: 6px 0 10px;
        }

        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1.6px solid #111; padding: 7px 6px; }
        thead th { font-weight: 800; text-align: center; }

        td.center { text-align: center; }
        td.left { text-align: left; }
        td.right { text-align: right; }

        .totalRow td { font-weight: 800; }
        .totalLabel { text-align: center; }

        .footNote {
          margin-top: 16px;
          font-size: 11px;
          text-align: center;
        }

        @media print {
          .noPrint { display: none !important; }
          .page { padding: 0; }
          .outer { border: 2px solid #111; }
        }
      `}</style>

      <div className="page">
        <div className="outer">
          {/* ✅ STATIC HEADER */}
          <div className="companyBox">
            <div className="logoBlock">{HEADER.logoText}</div>

            <div>
              <div className="companyName">{HEADER.name}</div>
              <div className="companySub">
                <div>{HEADER.sub1}</div>
                <div>
                  <strong>{HEADER.sub2}</strong>
                </div>
                <div>{HEADER.address}</div>
                <div>Phone No : {HEADER.phone}</div>
              </div>
            </div>

            <div className="rightTag">{HEADER.rightTop}</div>
          </div>

          <div className="divider" />

          <div className="title">EMPLOYEE LOAN DETAILS</div>

          {loading ? (
            <div style={{ padding: 14, textAlign: "center" }}>Loading...</div>
          ) : (
            <div style={{ overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 55 }}>S.No.</th>
                    <th style={{ width: 95 }}>EMP Code</th>
                    <th>EMP Name</th>
                    <th style={{ width: 110 }}>Loan Amount</th>
                    <th style={{ width: 110 }}>Deduction Amount</th>
                    <th style={{ width: 110 }}>Balance Amount</th>
                    <th style={{ width: 80 }}>Total Month</th>
                    <th style={{ width: 90 }}>Balance Month</th>
                    <th style={{ width: 130 }}>Location</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="center" style={{ padding: 18 }}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx}>
                        <td className="center">{idx + 1}</td>
                        <td className="center">{n(r.fld_Employeecode)}</td>
                        <td className="left">{n(r.fld_Employee)}</td>
                        <td className="right">{money(r.fld_LoanAmount)}</td>
                        <td className="right">{money(r.fld_AmountPerMonth)}</td>
                        <td className="right">{money(r.fld_BalanceAmount)}</td>
                        <td className="center">{n(r.fld_TotalMonth)}</td>
                        <td className="center">{n(r.fld_balancemonth)}</td>
                        <td className="center">{n(r.fld_BranchName)}</td>
                      </tr>
                    ))
                  )}

                  {rows.length > 0 && (
                    <tr className="totalRow">
                      <td colSpan={5} className="totalLabel">
                        Total Amount
                      </td>
                      <td className="right">{money(totalBalanceAmount)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="footNote">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>

        <div className="noPrint" style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => window.print()} style={{ padding: "10px 18px", cursor: "pointer" }}>
            Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLoanReportPrint;